import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, money, formatDate } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SignaturePad } from "@/components/SignaturePad";
import { FileText, Plus, Printer, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const defaultTerms = {
  fr: `1. Le bailleur loue au locataire le local désigné ci-dessus.\n2. Le loyer est payable d'avance le 1er de chaque mois.\n3. Un dépôt de garantie est versé à la signature et restitué en fin de bail, déduction faite des éventuels dommages.\n4. Le locataire s'engage à maintenir le local en bon état.\n5. Tout préavis de départ doit être donné 30 jours à l'avance.`,
  th: `1. ผู้ให้เช่าตกลงให้ผู้เช่าเช่าห้องพักตามที่ระบุข้างต้น\n2. ค่าเช่าชำระล่วงหน้าทุกวันที่ 1 ของเดือน\n3. ผู้เช่าวางเงินประกันในวันลงนามและจะได้รับคืนเมื่อสิ้นสุดสัญญา หักด้วยค่าเสียหาย (ถ้ามี)\n4. ผู้เช่าตกลงดูแลรักษาห้องพักให้อยู่ในสภาพดี\n5. การแจ้งย้ายออกต้องแจ้งล่วงหน้า 30 วัน`,
  en: `1. The landlord rents to the tenant the unit designated above.\n2. Rent is payable in advance on the 1st of each month.\n3. A security deposit is paid at signing and returned at the end of the lease, less any damages.\n4. The tenant agrees to keep the unit in good condition.\n5. Notice to vacate must be given 30 days in advance.`,
};
const langLabel = { fr: "Français", th: "ไทย", en: "English" };
const LANDLORD = "Alain — Maison Thaï, Phuket";

export default function Documents() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const canRefund = user?.role !== "reception";
  const qc = useQueryClient();
  const { data: reservations = [] } = useQuery({ queryKey: ["reservations"], queryFn: () => api.get("/reservations").then((r) => r.data) });
  const { data: tenants = [] } = useQuery({ queryKey: ["tenants"], queryFn: () => api.get("/tenants").then((r) => r.data) });
  const { data: units = [] } = useQuery({ queryKey: ["units"], queryFn: () => api.get("/units").then((r) => r.data) });
  const { data: contracts = [] } = useQuery({ queryKey: ["contracts"], queryFn: () => api.get("/contracts").then((r) => r.data) });
  const { data: refunds = [] } = useQuery({ queryKey: ["refunds"], queryFn: () => api.get("/refunds").then((r) => r.data) });

  const [contractForm, setContractForm] = useState(null);
  const [refundForm, setRefundForm] = useState(null);
  const [preview, setPreview] = useState(null); // {type, data}

  const resById = (id) => reservations.find((r) => r.id === id);
  const tenantById = (id) => tenants.find((x) => x.id === id);
  const unitById = (id) => units.find((x) => x.id === id);
  const ctx = (rid) => {
    const r = resById(rid); if (!r) return {};
    return { res: r, tenant: tenantById(r.tenant_id), unit: unitById(r.unit_id) };
  };

  const openContract = () => setContractForm({ reservation_id: "", language: lang === "th" ? "th" : "th", terms: defaultTerms.th, tenant_signature: "", landlord_signature: "" });
  const openRefund = () => setRefundForm({ reservation_id: "", deposit_amount: 0, deductions: 0, refund_amount: 0, reason: "", method: "transfer", tenant_signature: "", landlord_signature: "" });

  const saveContract = async () => {
    if (!contractForm.reservation_id) { toast.error(t("doc.selectRes")); return; }
    const { data } = await api.post("/contracts", contractForm);
    qc.invalidateQueries({ queryKey: ["contracts"] });
    setContractForm(null);
    toast.success(t("doc.saved"));
    setPreview({ type: "contract", data });
  };

  const saveRefund = async () => {
    if (!refundForm.reservation_id) { toast.error(t("doc.selectRes")); return; }
    const payload = { ...refundForm, deposit_amount: Number(refundForm.deposit_amount), deductions: Number(refundForm.deductions), refund_amount: Number(refundForm.refund_amount) };
    const { data } = await api.post("/refunds", payload);
    qc.invalidateQueries({ queryKey: ["refunds"] }); qc.invalidateQueries({ queryKey: ["payments"] }); qc.invalidateQueries({ queryKey: ["stats"] });
    setRefundForm(null);
    toast.success(t("doc.saved"));
    setPreview({ type: "refund", data });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-teak">{t("doc.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("doc.subtitle")}</p>
      </div>

      <Tabs defaultValue="contracts">
        <TabsList>
          <TabsTrigger value="contracts" data-testid="tab-contracts"><FileText className="w-4 h-4 mr-1" />{t("doc.contracts")}</TabsTrigger>
          <TabsTrigger value="refunds" data-testid="tab-refunds"><ShieldCheck className="w-4 h-4 mr-1" />{t("doc.refunds")}</TabsTrigger>
        </TabsList>

        <TabsContent value="contracts" className="mt-4 space-y-4">
          <Button onClick={openContract} className="bg-teak hover:bg-teak-hover text-white" data-testid="new-contract-btn"><Plus className="w-4 h-4 mr-1" />{t("doc.newContract")}</Button>
          {contracts.length === 0 ? <Card className="p-10 text-center text-muted-foreground border-dashed">{t("doc.noContract")}</Card> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {contracts.map((c) => { const { tenant, unit } = ctx(c.reservation_id); return (
                <Card key={c.id} className="p-5 border-border" data-testid={`contract-card-${c.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <Badge>{unit?.number || "—"}</Badge>
                    <span className="text-xs text-muted-foreground">{langLabel[c.language]}</span>
                  </div>
                  <div className="font-medium">{tenant ? `${tenant.first_name} ${tenant.last_name}` : "—"}</div>
                  <div className="text-xs text-muted-foreground mt-1">{t("doc.signedOn")} {formatDate(c.signed_at)}</div>
                  <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => setPreview({ type: "contract", data: c })} data-testid={`contract-view-${c.id}`}>
                    <Printer className="w-4 h-4 mr-1" />{t("doc.preview")}
                  </Button>
                </Card>
              ); })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="refunds" className="mt-4 space-y-4">
          {canRefund && <Button onClick={openRefund} className="bg-teak hover:bg-teak-hover text-white" data-testid="new-refund-btn"><Plus className="w-4 h-4 mr-1" />{t("doc.newRefund")}</Button>}
          {refunds.length === 0 ? <Card className="p-10 text-center text-muted-foreground border-dashed">{t("doc.noRefund")}</Card> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {refunds.map((c) => { const { tenant, unit } = ctx(c.reservation_id); return (
                <Card key={c.id} className="p-5 border-border" data-testid={`refund-card-${c.id}`}>
                  <div className="flex items-center justify-between mb-2"><Badge>{unit?.number || "—"}</Badge><span className="text-sm font-semibold">{money(c.refund_amount)}</span></div>
                  <div className="font-medium">{tenant ? `${tenant.first_name} ${tenant.last_name}` : "—"}</div>
                  <div className="text-xs text-muted-foreground mt-1">{formatDate(c.date)}</div>
                  <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => setPreview({ type: "refund", data: c })} data-testid={`refund-view-${c.id}`}>
                    <Printer className="w-4 h-4 mr-1" />{t("doc.preview")}
                  </Button>
                </Card>
              ); })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Contract form */}
      <Dialog open={!!contractForm} onOpenChange={(o) => !o && setContractForm(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("doc.newContract")}</DialogTitle></DialogHeader>
          {contractForm && (
            <div className="space-y-4">
              <div className="space-y-2"><Label>{t("doc.selectRes")}</Label>
                <Select value={contractForm.reservation_id} onValueChange={(v) => setContractForm({ ...contractForm, reservation_id: v })}>
                  <SelectTrigger data-testid="contract-res-select"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                  <SelectContent>{reservations.map((r) => { const tn = tenantById(r.tenant_id); const u = unitById(r.unit_id); return <SelectItem key={r.id} value={r.id}>{u?.number} · {tn ? `${tn.first_name} ${tn.last_name}` : "—"}</SelectItem>; })}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>{t("doc.language")}</Label>
                <Select value={contractForm.language} onValueChange={(v) => setContractForm({ ...contractForm, language: v, terms: defaultTerms[v] })}>
                  <SelectTrigger data-testid="contract-lang-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="th">ไทย</SelectItem><SelectItem value="fr">Français</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>{t("doc.terms")}</Label><Textarea rows={7} value={contractForm.terms} onChange={(e) => setContractForm({ ...contractForm, terms: e.target.value })} data-testid="contract-terms" className="font-thai" /></div>
              <SignaturePad label={t("doc.tenantSign")} testId="contract-tenant-sign" onChange={(v) => setContractForm({ ...contractForm, tenant_signature: v })} />
              <SignaturePad label={t("doc.landlordSign")} testId="contract-landlord-sign" onChange={(v) => setContractForm({ ...contractForm, landlord_signature: v })} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setContractForm(null)}>{t("common.cancel")}</Button>
            <Button onClick={saveContract} className="bg-teak hover:bg-teak-hover text-white" data-testid="contract-save-btn">{t("doc.generate")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund form */}
      <Dialog open={!!refundForm} onOpenChange={(o) => !o && setRefundForm(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("doc.newRefund")}</DialogTitle></DialogHeader>
          {refundForm && (
            <div className="space-y-4">
              <div className="space-y-2"><Label>{t("doc.selectRes")}</Label>
                <Select value={refundForm.reservation_id} onValueChange={(v) => { const r = resById(v); setRefundForm({ ...refundForm, reservation_id: v, deposit_amount: r?.deposit_amount || 0, refund_amount: r?.deposit_amount || 0 }); }}>
                  <SelectTrigger data-testid="refund-res-select"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                  <SelectContent>{reservations.map((r) => { const tn = tenantById(r.tenant_id); const u = unitById(r.unit_id); return <SelectItem key={r.id} value={r.id}>{u?.number} · {tn ? `${tn.first_name} ${tn.last_name}` : "—"}</SelectItem>; })}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label>{t("doc.depositAmount")}</Label><Input type="number" value={refundForm.deposit_amount} onChange={(e) => { const dep = Number(e.target.value); setRefundForm({ ...refundForm, deposit_amount: dep, refund_amount: dep - Number(refundForm.deductions) }); }} data-testid="refund-deposit" /></div>
                <div className="space-y-2"><Label>{t("doc.deductions")}</Label><Input type="number" value={refundForm.deductions} onChange={(e) => { const ded = Number(e.target.value); setRefundForm({ ...refundForm, deductions: ded, refund_amount: Number(refundForm.deposit_amount) - ded }); }} data-testid="refund-deductions" /></div>
                <div className="space-y-2"><Label>{t("doc.refundAmount")}</Label><Input type="number" value={refundForm.refund_amount} onChange={(e) => setRefundForm({ ...refundForm, refund_amount: Number(e.target.value) })} data-testid="refund-amount" /></div>
              </div>
              <div className="space-y-2"><Label>{t("doc.reason")}</Label><Textarea rows={2} value={refundForm.reason} onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} data-testid="refund-reason" /></div>
              <SignaturePad label={t("doc.tenantSign")} testId="refund-tenant-sign" onChange={(v) => setRefundForm({ ...refundForm, tenant_signature: v })} />
              <SignaturePad label={t("doc.landlordSign")} testId="refund-landlord-sign" onChange={(v) => setRefundForm({ ...refundForm, landlord_signature: v })} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundForm(null)}>{t("common.cancel")}</Button>
            <Button onClick={saveRefund} className="bg-teak hover:bg-teak-hover text-white" data-testid="refund-save-btn">{t("doc.generate")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview / print */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {preview && (
            <>
              <div className="print-area font-thai text-[#2A2A2A]">
                {preview.type === "contract" ? <ContractDoc c={preview.data} ctx={ctx(preview.data.reservation_id)} t={t} /> : <RefundDoc c={preview.data} ctx={ctx(preview.data.reservation_id)} t={t} />}
              </div>
              <DialogFooter className="no-print">
                <Button variant="outline" onClick={() => setPreview(null)}>{t("common.close")}</Button>
                <Button onClick={() => window.print()} className="bg-teak hover:bg-teak-hover text-white" data-testid="print-btn"><Printer className="w-4 h-4 mr-1" />{t("common.print")}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Badge = ({ children }) => <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-teak/10 text-teak">{children}</span>;

const Row = ({ k, v }) => <div className="flex justify-between py-1.5 border-b border-dashed border-[#E8E8E4]"><span className="text-[#6B6B6B]">{k}</span><span className="font-medium">{v}</span></div>;

const SignBlock = ({ label, sig }) => (
  <div className="text-center">
    <div className="h-20 flex items-end justify-center border-b border-[#2A2A2A] mb-2">
      {sig ? <img src={sig} alt="" className="h-16 object-contain" /> : null}
    </div>
    <div className="text-xs text-[#6B6B6B]">{label}</div>
  </div>
);

function ContractDoc({ c, ctx, t }) {
  const { res, tenant, unit } = ctx;
  return (
    <div className="p-6">
      <div className="text-center mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-terracotta">Maison Thaï · Phuket</div>
        <h2 className="text-2xl font-semibold text-teak mt-1">{t("doc.contract")}</h2>
        <div className="text-sm text-[#6B6B6B]">{langLabel[c.language]}</div>
      </div>
      <div className="grid grid-cols-2 gap-x-8 text-sm mb-5">
        <Row k={t("doc.landlord")} v={LANDLORD} />
        <Row k={t("doc.tenant")} v={tenant ? `${tenant.first_name} ${tenant.last_name}` : "—"} />
        <Row k={t("res.unit")} v={unit ? `${unit.number} · ${unit.name}` : "—"} />
        <Row k={t("res.type")} v={res ? t(`res.${res.rental_type}`) : "—"} />
        <Row k={t("res.start")} v={formatDate(res?.start_date)} />
        <Row k={t("res.end")} v={res?.end_date ? formatDate(res.end_date) : "—"} />
        <Row k={t("res.rent")} v={money(res?.rent_amount)} />
        <Row k={t("res.deposit")} v={money(res?.deposit_amount)} />
        {tenant?.id_number ? <Row k={t("tenants.idNumber")} v={tenant.id_number} /> : null}
        {tenant?.nationality ? <Row k={t("tenants.nationality")} v={tenant.nationality} /> : null}
      </div>
      <div className="whitespace-pre-line text-sm leading-relaxed mb-8 p-4 bg-[#FAFAF8] rounded-md">{c.terms}</div>
      <div className="grid grid-cols-2 gap-10 mt-10">
        <SignBlock label={t("doc.tenant")} sig={c.tenant_signature} />
        <SignBlock label={t("doc.landlord")} sig={c.landlord_signature} />
      </div>
      <div className="text-center text-xs text-[#6B6B6B] mt-6">{t("doc.signedOn")} {formatDate(c.signed_at)}</div>
    </div>
  );
}

function RefundDoc({ c, ctx, t }) {
  const { res, tenant, unit } = ctx;
  return (
    <div className="p-6">
      <div className="text-center mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-terracotta">Maison Thaï · Phuket</div>
        <h2 className="text-2xl font-semibold text-teak mt-1">{t("doc.receipt")}</h2>
      </div>
      <div className="grid grid-cols-2 gap-x-8 text-sm mb-5">
        <Row k={t("doc.landlord")} v={LANDLORD} />
        <Row k={t("doc.tenant")} v={tenant ? `${tenant.first_name} ${tenant.last_name}` : "—"} />
        <Row k={t("res.unit")} v={unit ? unit.number : "—"} />
        <Row k={t("fin.date")} v={formatDate(c.date)} />
        <Row k={t("doc.depositAmount")} v={money(c.deposit_amount)} />
        <Row k={t("doc.deductions")} v={money(c.deductions)} />
      </div>
      <div className="flex justify-between items-center p-4 bg-[#EAF4F0] rounded-md text-lg font-semibold text-teak mb-4">
        <span>{t("doc.refundAmount")}</span><span>{money(c.refund_amount)}</span>
      </div>
      {c.reason ? <div className="text-sm mb-8"><span className="text-[#6B6B6B]">{t("doc.reason")}: </span>{c.reason}</div> : null}
      <div className="grid grid-cols-2 gap-10 mt-12">
        <SignBlock label={t("doc.tenant")} sig={c.tenant_signature} />
        <SignBlock label={t("doc.landlord")} sig={c.landlord_signature} />
      </div>
    </div>
  );
}
